/**
 * Cliente consolidado pra chamar o backend `proxyPortal`
 * (`functions/src/proxy/portal.ts`, deployado em us-south1).
 *
 * Por quê um cliente dedicado:
 *   - Centraliza a URL base e o pattern `VITE_FIREBASE_PROJECT_ID`
 *   - Anexa o Bearer token do `firebase.auth().currentUser` quando existe
 *     (endpoints públicos como `/specialties` continuam funcionando sem ele;
 *     endpoints protegidos ganham o header automaticamente)
 *   - Normaliza erros em `PortalApiError` com `.status` e `.code`, o que
 *     deixa os callers tratarem 403 NOT_LINKED e 409 SLOT_TAKEN sem
 *     parsing ad-hoc
 *
 * Histórico: na versão anterior o portal tentava chamar `/api/portal/...`
 * assumindo um rewrite de Firebase Hosting que nunca existiu — as
 * chamadas sempre caíam no fallback SPA (index.html), os componentes
 * renderizavam dados mockados, e o backend nunca era exercitado.
 */
import { auth } from '@/services/firebase/config';
import { appCheckHeader } from '@/services/firebase/appCheck';

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
const PORTAL_API = `https://us-south1-${PROJECT_ID}.cloudfunctions.net/proxyPortal`;

export class PortalApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'PortalApiError';
  }
}

async function portalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };

  // Anexa token quando houver usuário Firebase logado. Endpoints públicos
  // ignoram o header; endpoints autenticados verificam via verifyPatientToken.
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    } catch {
      // getIdToken pode falhar se o token estiver expirado — nesses casos
      // tratamos como chamada anônima e deixamos o backend responder 401.
    }
  }

  // App Check por último, DEPOIS do spread de `init.headers`: assim um
  // caller que passe headers próprios não consegue sobrescrever o token.
  // No-op quando App Check está desligado (ver services/firebase/appCheck).
  Object.assign(headers, await appCheckHeader());

  const res = await fetch(`${PORTAL_API}${path}`, { ...init, headers });
  if (!res.ok) {
    let errorBody: { error?: string; code?: string } = {};
    try {
      errorBody = await res.json();
    } catch {
      /* response sem JSON; cai no status/statusText */
    }
    throw new PortalApiError(
      res.status,
      errorBody.error || res.statusText || `HTTP ${res.status}`,
      errorBody.code,
    );
  }
  return res.json() as Promise<T>;
}

// ── Tipos de resposta ──────────────────────────────────────

export interface PortalSpecialty { id: string; name: string }
export interface PortalUnit { id: string; name: string; address: string }
/** Convênio retornado pelo /insurers (ativos da clínica). O front sempre
 *  injeta uma opção "Particular" sintética com id='PARTICULAR' antes
 *  desta lista — não vem do backend. */
export interface PortalInsurer { id: string; name: string }
/** Profissional retornado pelo /professionals (filtrado por especialidade).
 *  `unitIds` é exposto pra o cliente filtrar a tela de unidades sem
 *  round-trip extra (mostra só as unidades onde aquele médico atende). */
export interface PortalProfessional { id: string; name: string; unitIds: string[] }
/** Identidade pública da clínica — alimenta o header do portal.
 *  Campos vazios significam "usar fallback no front" (placeholder
 *  com inicial do nome / nome estático). */
export interface PortalClinicInfo {
  name: string;
  logoUrl: string;
  tagline: string;
  /** Só dígitos (DDD + número), vazio quando a clínica não cadastrou.
   *  Usado pra oferecer contato quando as vagas online do dia acabam. */
  phone: string;
  /** Clínica revisa TODO agendamento vindo do portal: até consulta comum
   *  nasce PENDENTE. A tela de confirmação muda inteira por causa disto. */
  requireApprovalForExternalBookings?: boolean;
  /** Antecedência mínima (horas) pro paciente cancelar sozinho. 0 = sem limite. */
  cancellationMinHours?: number;
  /** Duração do slot, em minutos — o resumo mostra "13:00 – 13:30". */
  defaultDuration?: number;
  /** Valor da consulta PARTICULAR (reais). null quando a clínica não liga
   *  "mostrar valor no portal" ou não cadastrou o preço. */
  consultaPrice?: number | null;
  /** O que a clínica de fato manda pelo WhatsApp (chave ligada E credencial
   *  real). Ausente = nada é prometido na tela. */
  notices?: { confirmation?: boolean; reminder?: boolean };
}
export interface PortalDateOption {
  date: string;        // 'YYYY-MM-DD'
  displayDate: string; // 'Seg, 15/04'
  /** Vagas online livres. Sempre 0 quando `quotaFull` — a clínica atende
   *  nesse dia, mas a cota de agendamento pela internet acabou. */
  slotsCount: number;
  /** true = dia de atendimento com a cota online esgotada. Aparece na lista
   *  desabilitado, pra o paciente saber que existe atendimento e poder pedir
   *  encaixe, em vez de concluir que a clínica não abre nesse dia. */
  quotaFull?: boolean;
}
export interface PortalDoctorSlotGroup {
  doctorId: string;
  doctorName: string;
  specialty: string;
  availableSlots: string[];
}
export interface PortalAppointment {
  id: string;
  unitId: string;
  unitName: string;        // resolvido server-side via resolveUnitNames
  professionalId: string;
  professionalName: string;// resolvido server-side via resolveProfessionalNames
  patientId: string;
  patientName?: string;
  type: string;
  status: string;
  date: number;
  startTime: string;
  endTime: string;
}
export interface PortalPrescription {
  id: string;
  type: string;            // 'LENS' | 'MEDICATION' | 'EXAM_REQUEST' | 'ATTESTATION' | 'RECEIPT' | 'PRESCRIPTION' | 'CERTIFICATE' | 'SURGERY' | 'REFERRAL'
  content?: unknown;
  createdAt: number;
  professionalId: string;
  professionalName: string;
  signatureStatus?: 'UNSIGNED' | 'SIGNED_ICP' | 'SIGNED_MANUAL';
}
export interface PortalMe {
  uid: string;
  email: string | null;
  displayName: string | null;
  linkedPatientId: string | null;
  patientName: string | null;
  /** Data sugerida de retorno (epoch ms) definida pelo médico, ou null. */
  recommendedReturnAt?: number | null;
  /** Quando o médico gravou essa recomendação (epoch ms), ou null. */
  recommendedReturnSetAt?: number | null;
  /** Convênios do prontuário (só os IDs). Vazio sem vínculo. */
  insurerIds?: string[];
}

/**
 * Pedido de lente de contato visto pelo paciente (Sprint 4.10).
 *
 * Shape mínimo — só o que o dashboard precisa renderizar. O `sku`
 * vem completo porque o card mostra BC/DIA/SPH/CYL do SKU, mas os
 * campos internos (familyId, prefix, etc.) não são expostos na UI
 * — só estão presentes pra facilitar evoluções futuras sem mudar
 * o contrato.
 *
 * `status` fica como string em vez de union ('CONFIRMED' | 'DELIVERED'
 * | 'CANCELLED') pra proteger o portal de mudanças futuras no enum
 * server-side — a UI mapeia para label friendly e fallback genérico.
 */
export interface PortalLensOrder {
  id: string;
  status: string;
  eye: string; // 'OD' | 'OE'
  familyName: string;
  ean: string;
  sku: {
    ean: string;
    familyName: string;
    descricao: string;
    bc: number;
    dia: number;
    sph: number;
    cyl: number;
    axis: number;
    add: number | null;
    tipo?: string;
    marker?: string | null;
  } | null;
  spectacleRx: { sph: number; cyl: number; axis: number } | null;
  fallbackUsed: boolean;
  professionalName: string;
  createdAt: number | null;
  notes: string;
}

/**
 * Solicitação de lente de contato (paciente → médico). Sprint 4.11.
 *
 * Espelha o doc em `clinics/{id}/lensOrderRequests/{id}` mas só com os
 * campos que a UI do paciente precisa renderizar. `status` ficou como
 * string solta pra proteger o portal de adições futuras no enum
 * server-side (mesmo critério aplicado em `PortalLensOrder`).
 */
export interface PortalLensRequest {
  id: string;
  status: string; // 'PENDING' | 'APPROVED' | 'DECLINED' | 'EXPIRED'
  eye: string;    // 'OD' | 'OE'
  spectacleRx: { sph: number; cyl: number; axis: number } | null;
  vertexMm: number | null;
  preferredFamilyId: string | null;
  notes: string;
  sourcePrescriptionId: string | null;
  resultingOrderId: string | null;
  createdAt: number | null;
  reviewedAt: number | null;
}

/**
 * Bundle pra geração de PDF do pedido no portal (Sprint 4.10),
 * equivalente ao `PortalPrescriptionFull` mas com o shape de
 * LensOrder. Shape alinhado com o retorno de /lens-orders/:id/full.
 */
export interface PortalLensOrderFull {
  order: {
    id: string;
    clinicId: string;
    patientId: string;
    patientName: string;
    professionalId: string;
    professionalName: string;
    status: string;
    source: string;
    eye: string;
    spectacleRx: { sph: number; cyl: number; axis: number } | null;
    vertexMm: number | null;
    compensatedRx: { sph: number; cyl: number; axis: number } | null;
    roundedRx: { sph: number; cyl: number; axis: number } | null;
    fallbackUsed: boolean;
    sku: {
      ean: string;
      familyName: string;
      descricao: string;
      bc: number;
      dia: number;
      sph: number;
      cyl: number;
      axis: number;
      add: number | null;
      tipo?: string;
      marker?: string | null;
      prefix?: string;
      familyId?: string;
    } | null;
    notes: string;
    createdAt: number | null;
  };
  clinic: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logoUrl: string;
  };
  professional: {
    name: string;
    crm: string;
    crmState: string;
    specialty: string;
  };
  patient: {
    name: string;
  };
}

/**
 * Bundle completo que alimenta a geração de PDF no portal
 * (endpoint `/prescriptions/:id/full`, Sprint 4.6).
 *
 * Evita 4 fetches client-side pra montar o mesmo PDF que o staff
 * gera na ficha. Só os campos que o `PDFGenerator` precisa vêm
 * populados — o backend poda o resto pra não vazar dados sensíveis
 * do prontuário (ex.: CPF do paciente não é necessário pro layout
 * atual, então não é enviado).
 */
export interface PortalPrescriptionFull {
  prescription: {
    id: string;
    clinicId: string;
    patientId: string;
    professionalId: string;
    type: string;
    content: unknown;
    signatureStatus: 'UNSIGNED' | 'SIGNED_ICP' | 'SIGNED_MANUAL';
    createdAt: number | null;
  };
  clinic: {
    name: string;
    address: string; // já achatado server-side (street, number — city/state)
    phone: string;
    email: string;
    logoUrl: string;
  };
  professional: {
    name: string;
    crm: string;
    crmState: string;
    specialty: string;
  };
  patient: {
    name: string;
  };
}

// ── API pública ─────────────────────────────────────────────

export const PortalAPI = {
  /** Público. Especialidades distintas dos profissionais ativos da clínica. */
  specialties(clinicId: string) {
    return portalFetch<{ specialties: PortalSpecialty[] }>(
      `/specialties?clinicId=${encodeURIComponent(clinicId)}`,
    );
  },

  /** Público. Unidades ativas da clínica. */
  units(clinicId: string) {
    return portalFetch<{ units: PortalUnit[] }>(
      `/units?clinicId=${encodeURIComponent(clinicId)}`,
    );
  },

  /** Público. Convênios ativos cadastrados em /configuracoes →
   *  Convênios e TISS. Não inclui "Particular" — o portal injeta como
   *  primeira opção sintética antes da lista do backend. */
  insurers(clinicId: string) {
    return portalFetch<{ insurers: PortalInsurer[] }>(
      `/insurers?clinicId=${encodeURIComponent(clinicId)}`,
    );
  },

  /** Público. Identidade da clínica (nome + URL da logomarca) pra o
   *  header do portal. Campos vazios → front usa placeholder. */
  clinicInfo(clinicId: string) {
    return portalFetch<PortalClinicInfo>(
      `/clinic-info?clinicId=${encodeURIComponent(clinicId)}`,
    );
  },

  /** Público. Profissionais ativos da clínica que atendem a especialidade
   *  pedida. Inclui `unitIds` pra o cliente filtrar a tela de unidades
   *  pelos locais onde o médico realmente atende. */
  professionals(clinicId: string, specialtyId: string) {
    return portalFetch<{ professionals: PortalProfessional[] }>(
      `/professionals?clinicId=${encodeURIComponent(clinicId)}&specialtyId=${encodeURIComponent(specialtyId)}`,
    );
  },

  /**
   * Público. Datas com ≥1 vaga para (specialty + unit) nos próximos N dias
   * (padrão 14). Feito server-side em UMA query de appointments — evita os
   * 14 POSTs paralelos que o wizard teria que fazer client-side.
   */
  availableDates(body: {
    clinicId: string;
    specialtyId: string;
    unitId: string;
    daysAhead?: number;
    /** Quando setado, conta só vagas deste profissional. */
    professionalId?: string;
  }) {
    // `code` explica POR QUE a lista veio vazia — hoje só
    // 'ONLINE_BOOKING_DISABLED'. Sem ele o passo da data mostrava
    // "não encontramos dias de atendimento", como se a clínica não
    // atendesse, quando na verdade o agendamento online está desligado.
    return portalFetch<{ dates: PortalDateOption[]; code?: string }>('/available-dates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Público. Horários livres por médico em uma data específica. */
  compactSlots(body: {
    clinicId: string;
    specialtyId: string;
    unitId: string;
    date: string;
    shift?: 'MANHA' | 'TARDE';
    /** Quando setado, retorna só slots deste profissional. */
    professionalId?: string;
  }) {
    // `code` explica POR QUE a lista veio vazia: 'ONLINE_QUOTA_FULL' (as
    // vagas online do dia acabaram) ou 'ONLINE_BOOKING_DISABLED'. Sem ele
    // o paciente vê "nenhum horário" e não distingue isso de "o médico não
    // atende neste dia" — e desiste em vez de pedir encaixe.
    return portalFetch<{ slots: PortalDoctorSlotGroup[]; code?: string }>('/compact-slots', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Autenticado. Cria a consulta em `clinics/{id}/appointments` vinculada
   * ao paciente logado (linkedPatientId em portalUsers).
   *
   * Erros esperados:
   *   - 401 → token ausente/expirado (usuário precisa logar)
   *   - 403 code=NOT_LINKED → paciente não vinculou CPF ainda
   *   - 409 code=SLOT_TAKEN → horário foi ocupado entre a listagem e o clique
   */
  book(body: {
    date: string;
    startTime: string;
    professionalId: string;
    unitId: string;
    type?: string;
    /** Opcional. id de convênio cadastrado, ou 'PARTICULAR' (sentinel
     *  que o backend ignora, equivalente a omitir). */
    insurerId?: string;
    /** Lateralidade do procedimento — só com type 'PROCEDIMENTO'. */
    eye?: 'OD' | 'OE' | 'AO';
  }) {
    return portalFetch<{ success: boolean; appointmentId: string; endTime: string; status?: string }>(
      '/book',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /**
   * Público. Cria a conta do paciente: usuário no Firebase Auth + doc em
   * `clinics/{clinicId}/portalUsers/{uid}` com `linkedPatientId: null`.
   *
   * Depois de signup o frontend faz `signInWithEmailAndPassword` client-side
   * (o endpoint `/auth/login` antigo foi removido — ele não validava senha).
   *
   * Erros esperados:
   *   - 400 → email/senha/clinicId faltando
   *   - 409 → email já cadastrado no Firebase Auth
   */
  signup(body: { email: string; password: string; displayName?: string; clinicId: string }) {
    return portalFetch<{ success: boolean; uid: string; message: string }>(
      '/auth/signup',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /**
   * Público. Dispara um magic link por email (Sprint 4.8). A resposta
   * é sempre success mesmo se o email não existir — o backend não
   * revela existência de conta pra prevenir enumeration.
   *
   * `continueUrl` é pra onde o usuário volta após clicar no link —
   * default `https://dratento.com/portal/verify?clinicId=...`. Em dev
   * o frontend pode sobrescrever pra localhost.
   */
  requestMagicLink(body: { email: string; clinicId: string; continueUrl?: string }) {
    return portalFetch<{ success: boolean; message: string }>(
      '/auth/magic-link',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /** Autenticado. Vincula o UID do portal ao prontuário via CPF ou telefone. */
  /**
   * Autenticado (mas SEM exigir claim PACIENTE — o token de quem acabou de
   * entrar com Google ainda não tem claim nenhuma). "Adota" o usuário social:
   * seta `role: PACIENTE` + `clinicId` e garante o doc em `portalUsers`.
   * Depois do 200 o caller PRECISA forçar `getIdToken(true)` pra claim
   * entrar no token.
   *
   * Erros esperados:
   *   - 401 → sem token / token inválido
   *   - 403 STAFF_ACCOUNT → conta Google é da equipe; deslogar e orientar
   */
  socialInit(body: { clinicId: string }) {
    return portalFetch<{ success: boolean; created: boolean }>(
      '/auth/social-init',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /**
   * Público. Gera código OTP de 6 dígitos e envia pro WhatsApp do número.
   * `devLogged: true` = WhatsApp ainda não configurado; o código foi só
   * pro log da function (modo de teste do operador).
   *
   * Erros esperados: 400 (número inválido), 429 (limite de reenvio),
   * 502 (Meta recusou o envio).
   */
  whatsappRequest(body: { phone: string; clinicId: string }) {
    return portalFetch<{ success: boolean; devLogged?: boolean }>(
      '/auth/whatsapp/request',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /**
   * Público. Verifica o código e devolve um custom token do Firebase —
   * logar com `signInWithCustomToken(auth, token)`. A conta é criada na
   * hora se o número nunca entrou (claims PACIENTE já vêm no token).
   *
   * Erros esperados: 400/429 CODE_INVALID (código errado/expirado/esgotado),
   * 403 STAFF_ACCOUNT.
   */
  whatsappVerify(body: { phone: string; code: string; clinicId: string }) {
    return portalFetch<{ success: boolean; token: string }>(
      '/auth/whatsapp/verify',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /** `birthDate` no formato 'YYYY-MM-DD' — segundo fator do vínculo.
   *  Opcional no tipo só por compatibilidade com bundles antigos em cache;
   *  o backend confere sempre que vem, e vai passar a exigir. */
  identityLink(body: { cpf?: string; phone?: string; birthDate?: string }) {
    return portalFetch<{
      success: boolean;
      matched: boolean;
      patientId?: string;
      patientName?: string;
    }>('/identity/link', { method: 'POST', body: JSON.stringify(body) });
  },

  /**
   * Autenticado. Cria o prontuário de um paciente NOVO (que o
   * `/identity/link` não encontrou). O backend deduplica antes de criar e,
   * em qualquer sinal de ficha existente, devolve 409
   * MANUAL_VERIFICATION_REQUIRED em vez de gerar prontuário duplicado.
   */
  selfRegister(body: { name: string; cpf: string; birthDate: string; phone: string }) {
    return portalFetch<{
      success: boolean;
      created: boolean;
      patientId: string;
      patientName: string;
    }>('/identity/self-register', { method: 'POST', body: JSON.stringify(body) });
  },

  /** Autenticado. Consultas futuras (status não-terminal) do paciente logado. */
  myAppointments() {
    return portalFetch<{ appointments: PortalAppointment[] }>('/my-appointments');
  },

  /**
   * Autenticado. Cancela consulta do paciente logado (Sprint 4.7).
   *
   * Errros esperados:
   *   - 403 NOT_LINKED → sem vínculo de prontuário
   *   - 403 NOT_OWNER → consulta não pertence ao paciente logado
   *   - 404 → id inexistente
   *   - 409 ALREADY_CANCELLED → já está cancelada
   *   - 409 STATUS_LOCKED → em atendimento / concluída / faltou
   *   - 409 PAST_APPOINTMENT → data já passou
   */
  cancelAppointment(appointmentId: string, reason?: string) {
    return portalFetch<{ success: boolean; appointmentId: string }>(
      `/my-appointments/${encodeURIComponent(appointmentId)}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
    );
  },

  /** Autenticado. Receitas/exames do paciente logado, mais recentes primeiro. */
  myPrescriptions() {
    return portalFetch<{ prescriptions: PortalPrescription[] }>('/my-prescriptions');
  },

  /**
   * Autenticado. Bundle `{prescription, clinic, professional, patient}`
   * usado pelo portal pra gerar o PDF client-side via `PDFGenerator`.
   *
   * Ownership checada server-side (prescription.patientId ===
   * portalUser.linkedPatientId). Erros esperados:
   *   - 403 NOT_LINKED → paciente sem vínculo
   *   - 403 NOT_OWNER → tentou baixar prescrição de outro paciente
   *   - 404 → id inexistente
   */
  prescriptionFull(id: string) {
    return portalFetch<PortalPrescriptionFull>(`/prescriptions/${encodeURIComponent(id)}/full`);
  },

  /**
   * Autenticado. Perfil do portalUser + status de vinculação. Usado pelo
   * dashboard pra saber se deve mostrar o aviso "vincule seu CPF" e
   * qual nome usar na saudação (patientName > displayName).
   */
  me() {
    return portalFetch<PortalMe>('/me');
  },

  /**
   * Autenticado. Pedidos de lente de contato do paciente logado,
   * mais recentes primeiro (Sprint 4.10). Sem vínculo → array vazio.
   */
  myLensOrders() {
    return portalFetch<{ lensOrders: PortalLensOrder[] }>('/my-lens-orders');
  },

  /**
   * Autenticado. Bundle `{order, clinic, professional, patient}` usado
   * pelo portal pra gerar o PDF do pedido de lente client-side (mesmo
   * padrão de prescriptionFull). Ownership checada server-side; erros:
   *   - 403 NOT_LINKED → paciente sem vínculo
   *   - 403 NOT_OWNER → tentou baixar pedido de outro paciente
   *   - 404 → id inexistente
   */
  lensOrderFull(id: string) {
    return portalFetch<PortalLensOrderFull>(
      `/lens-orders/${encodeURIComponent(id)}/full`,
    );
  },

  /**
   * Autenticado. Solicitações de lente de contato do paciente logado
   * (status PENDING/APPROVED/DECLINED/EXPIRED), mais recentes primeiro.
   * Sem vínculo → array vazio.
   */
  myLensRequests() {
    return portalFetch<{ lensRequests: PortalLensRequest[] }>('/my-lens-requests');
  },

  /**
   * Autenticado. Cria uma solicitação de lente a partir de uma receita
   * existente do paciente (LENS ou PRESCRIPTION). O backend snapshota
   * sph/cyl/axis no doc, então edições posteriores na receita não
   * afetam a solicitação. Erros esperados:
   *   - 400 INVALID_BODY / NO_RX_DATA → receita sem valores válidos
   *   - 403 NOT_LINKED / NOT_OWNER
   *   - 404 PRESCRIPTION_NOT_FOUND
   *   - 409 INVALID_PRESCRIPTION_TYPE → tipo da receita não aceito
   */
  submitLensRequest(body: {
    sourcePrescriptionId: string;
    /**
     * 'AMBOS' cria DUAS solicitações no backend (uma por olho) ligadas por
     * `pairRequestId`. A resposta retorna a lista completa de ids criados;
     * `requestId` é o primeiro pra compatibilidade.
     */
    eye: 'OD' | 'OE' | 'AMBOS';
    preferredFamilyId?: string;
    notes?: string;
  }) {
    return portalFetch<{
      success: boolean;
      requestId: string;
      requestIds?: string[];
      pairRequestId?: string | null;
    }>(
      '/submit-lens-request',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /**
   * Autenticado. Exporta todo o acervo do paciente como JSON (LGPD).
   * Retorna o body cru — o caller (dashboard) empacota em Blob pra download.
   */
  lgpdExport() {
    return portalFetch<Record<string, unknown>>('/lgpd/export', {
      method: 'POST',
    });
  },

  /** Autenticado. Registra pedido de exclusão; a clínica processa manualmente. */
  lgpdDelete(reason?: string) {
    return portalFetch<{ success: boolean; message?: string }>('/lgpd/delete', {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'Solicitação via portal do paciente' }),
    });
  },
};
